// ─────────────────────────────────────────────────────────────────────────────
// features/analysis/rawDataPreview — the SHARED "RawData Preview" datasource +
// lookup + download, extracted verbatim from SDMS Result Validation
// (SdmsPackageDetail) on 2026-09-10 so Result Tracking (and future forms) reuse
// the SAME logic instead of re-implementing it (standing rule 8, "do not
// duplicate"). Behaviour is byte-for-byte what SDMS Result Validation did:
//   • useRawData(batchOid)         — fetch a batch's raw-data availability
//                                    (GET .../result-validation/{batchOid}/raw-data).
//   • resolveRawDataFileOid(info,…)— which FileData.Oid a row's Preview downloads
//                                    (single-instrument → the batch's newest file;
//                                    MultipleInstruments → the newest file for that
//                                    exact SysSampleCode).
//   • downloadRawDataFile(oid,name)— stream the file through the SAME authenticated
//                                    blob endpoint the Attachments tab uses
//                                    (GET .../result-validation/attachments/{oid}/download).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { api } from '../../api/client';

// FileOid is FileData.Oid — the key for GET .../attachments/{fileOid}/download.
export interface ApiRawDataFile {
  sysSampleCode: string | null;
  fileOid: string | null;
  fileName: string | null;
  createdDate: string | null;
}
export interface ApiRawDataInfo {
  isMultiInstrument: boolean;
  files: ApiRawDataFile[];
}

const EMPTY: ApiRawDataInfo = { isMultiInstrument: false, files: [] };

/** Fetch the batch's raw-data availability (ALPACA "RawData" Preview column,
 *  DataPackageViewController.PreViewRawData_Execute). Files arrive newest-first
 *  from the API; a MultipleInstruments template matches per row by SysSampleCode,
 *  otherwise every row shares files[0]. */
export function useRawData(batchOid: string): ApiRawDataInfo {
  const [info, setInfo] = useState<ApiRawDataInfo>(EMPTY);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: ApiRawDataInfo }>(
          `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/raw-data`,
        );
        if (!cancelled) setInfo(res.data?.data ?? EMPTY);
      } catch (e) {
        console.warn('[RawData] fetch failed for', batchOid, e);
        if (!cancelled) setInfo(EMPTY);
      }
    })();
    return () => { cancelled = true; };
  }, [batchOid]);
  return info;
}

/** Fetch raw-data availability for MANY batches at once (Result Tracking spans a
 *  whole job / window across several analytical batches). Returns a map keyed by
 *  batchOid; each entry is the SAME {@link ApiRawDataInfo} shape useRawData yields,
 *  so {@link resolveRawDataFileOid} resolves a row from map.get(row.uqabid). */
export function useRawDataForBatches(batchOids: readonly string[]): Map<string, ApiRawDataInfo> {
  const [map, setMap] = useState<Map<string, ApiRawDataInfo>>(new Map());
  // Stable key so the effect only re-runs when the DISTINCT set of batches changes.
  const key = [...new Set(batchOids.filter(Boolean))].sort().join('|');
  useEffect(() => {
    let cancelled = false;
    const ids = key ? key.split('|') : [];
    if (ids.length === 0) { setMap(new Map()); return; }
    (async () => {
      const next = new Map<string, ApiRawDataInfo>();
      await Promise.all(ids.map(async (id) => {
        try {
          const res = await api.get<{ success: boolean; data: ApiRawDataInfo }>(
            `/api/v1/analysis/result-validation/${encodeURIComponent(id)}/raw-data`,
          );
          next.set(id, res.data?.data ?? EMPTY);
        } catch (e) {
          console.warn('[RawData] fetch failed for', id, e);
          next.set(id, EMPTY);
        }
      }));
      if (!cancelled) setMap(next);
    })();
    return () => { cancelled = true; };
  }, [key]);
  return map;
}

/** Resolves the FileData Oid (if any) a given SysSampleCode's "Preview" link should
 *  download — mirrors PreViewRawData_Execute's own selection: a MultipleInstruments
 *  template matches the most-recent file for that exact sample; otherwise every
 *  sample shares the batch's single most-recent file. `files` is already
 *  newest-first from the API. */
export function resolveRawDataFileOid(info: ApiRawDataInfo | undefined, sysSampleCode: string): string | null {
  if (!info || info.files.length === 0) return null;
  if (!info.isMultiInstrument) return info.files[0]!.fileOid;
  return info.files.find((f) => f.sysSampleCode === sysSampleCode)?.fileOid ?? null;
}

/** Streams the raw-data file through the SAME authenticated blob-download path the
 *  Attachments tab uses (a plain <a href> would 401 → /login, because the JWT lives
 *  in an Authorization header, not a cookie). Throws on failure — the caller shows
 *  the notification (keeping each form's exact wording). */
export async function downloadRawDataFile(fileOid: string, fileName: string | null | undefined): Promise<void> {
  const res = await api.get<Blob>(
    `/api/v1/analysis/result-validation/attachments/${encodeURIComponent(fileOid)}/download`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'raw-data';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
